import { AxiosAdapter, RawAxiosRequestHeaders } from "axios";
import { AxiosClient, BaseParams } from "./client.types.js";
import { getAccount } from "./private/Accounts/GetAccount/getAccount.client.js";
import { getAccountLoginUrl } from "./private/Accounts/GetAccountLoginUrl/getAccountLoginUrl.client.js";
import { getAccountRequirements } from "./private/Accounts/GetAccountRequirements/getAccountRequirements.client.js";
import { getNextBalance } from "./private/Accounts/GetNextBalance/getNextBalance.client.js";
import { getOnboardingUrl } from "./private/Accounts/GetOnboardingUrl/getOnboardingUrl.client.js";
import { getStripeSettings } from "./private/Accounts/GetStripeSettings/getStripeSettings.client.js";
import { getUpdateUrl } from "./private/Accounts/GetUpdateUrl/getUpdateUrl.client.js";
import { exportAffiliatesData } from "./private/Affiliates/ExportAffiliatesData/exportAffiliatesData.client.js";
import { getAffiliatePayments } from "./private/Affiliates/GetAffiliatePayments/getAffiliatePayments.client.js";
import { getAffiliates } from "./private/Affiliates/GetAffiliates/getAffiliates.client.js";
import { getAffiliatesLinkedinIds } from "./private/Affiliates/GetAffiliatesLinkedinIds/getAffiliatesLinkedinIds.client.js";
import { getAffiliatesSummaryFromLastLogin } from "./private/Affiliates/GetAffiliatesSummaryFromLastLogin/getAffiliatesSummaryFromLastLogin.client.js";
import { getAffiliateSummary } from "./private/Affiliates/GetAffiliateSummary/getAffiliateSummary.client.js";
import { getAffiliationErrors } from "./private/AffiliationErrors/GetAffiliationErrors/getAffiliationErrors.client.js";
import { setAffiliationErrorsProcessed } from "./private/AffiliationErrors/SetAffiliationErrorsProcessed/setAffiliationErrorsProcessed.client.js";
import { createAffiliation } from "./private/Ambassadors/Affiliation/CreateAffiliation/createAffiliation.client.js";
import { getAffiliation } from "./private/Ambassadors/Affiliation/GetAffiliation/getAffiliation.client.js";
import { acceptContract } from "./private/Ambassadors/Contracts/AcceptContract/acceptContract.client.js";
import { getLanguage } from "./private/Ambassadors/Language/GetLanguage/getLanguage.client.js";
import { setLanguage } from "./private/Ambassadors/Language/SetLanguage/setLanguage.client.js";
import { createLink } from "./private/Ambassadors/Links/CreateLink/createLink.client.js";
import { deleteLink } from "./private/Ambassadors/Links/DeleteLink/deleteLink.client.js";
import { getLinks } from "./private/Ambassadors/Links/GetLinks/getLinks.client.js";
import { getSummary } from "./private/Ambassadors/Links/GetSummary/getSummary.client.js";
import { updateLink } from "./private/Ambassadors/Links/UpdateLink/updateLink.client.js";
import { getPayments } from "./private/Ambassadors/Payment/GetPayments/getPayments.client.js";
import { getPreviousPaymentAmount } from "./private/Ambassadors/Payment/GetPreviousPaymentAmount/getPreviousPaymentAmount.client.js";
import { getIntercomIdentifiers } from "./private/Ambassadors/Profile/GetIntercomIdentifiers/getIntercomIdentifiers.client.js";
import { getProfile } from "./private/Ambassadors/Profile/GetProfile/getProfile.client.js";
import { updatePassword } from "./private/Ambassadors/Profile/UpdatePassword/updatePassword.client.js";
import { updateProfile } from "./private/Ambassadors/Profile/UpdateProfile/updateProfile.client.js";
import { sendInviteToUser } from "./private/Ambassadors/SendInviteToUser/sendInviteToUser.client.js";
import { startStripeOnboarding } from "./private/Ambassadors/StripeAccount/StartStripeOnboarding/startStripeOnboarding.client.js";
import { confirmTransfer } from "./private/Ambassadors/Wallets/ConfirmTransfer/ConfirmTransfer.client.js";
import { getWallet } from "./private/Ambassadors/Wallets/GetWallet/getWallet.client.js";
import { prepareGeneratedInvoice } from "./private/Ambassadors/Wallets/PrepareGeneratedInvoice/prepareGeneratedInvoice.client.js";
import { verifyInvoice } from "./private/Ambassadors/Wallets/VerifyInvoice/VerifyInvoice.client.js";
import { linkAccount } from "./private/Waalaxy/LinkAccount/linkAccount.client.js";
import { checkPasswordToken } from "./public/Ambassadors/CheckPasswordToken/checkPasswordToken.client.js";
import { forgetPassword } from "./public/Ambassadors/ForgetPassword/forgetPassword.client.js";
import { impersonate } from "./public/Ambassadors/Impersonate/impersonate.client.js";
import { login } from "./public/Ambassadors/Login/login.client.js";
import { resetPassword } from "./public/Ambassadors/ResetPassword/resetPassword.client.js";
import { signUp } from "./public/Ambassadors/SignUp/signUp.client.js";
import { getCountryList } from "./public/Stripe/GetCountryList/getCountryList.client.js";
import { webhooks } from "./public/Stripe/Webhooks/webhooks.client.js";
import { webhooksAccount } from "./public/Stripe/WebhooksAccount/webhooksAccount.client.js";
// [INSERT IMPORTS]
export const getAxiosClient = (baseParams: BaseParams): AxiosClient => {
    const baseHeaders = { "Bypass-Tunnel-Reminder": "anything" };
    const params = { ...baseParams, headers: { ...baseHeaders, ...baseParams.headers } };
    return {
        setHeaders: (headers: RawAxiosRequestHeaders) => {
            return getAxiosClient({ ...baseParams, headers });
        },
        setBaseUrl: (baseUrl: string) => {
            return getAxiosClient({ ...baseParams, baseUrl });
        },
        setAxiosAdapter: (adapter: AxiosAdapter) => {
            return getAxiosClient({ ...baseParams, adapter });
        },
        private: {
            accounts: {
                getAccount: getAccount(params),
                getAccountLoginUrl: getAccountLoginUrl(params),
                getAccountRequirements: getAccountRequirements(params),
                getNextBalance: getNextBalance(params),
                getOnboardingUrl: getOnboardingUrl(params),
                getStripeSettings: getStripeSettings(params),
                getUpdateUrl: getUpdateUrl(params),
            },
            affiliates: {
                exportAffiliatesData: exportAffiliatesData(params),
                getAffiliatePayments: getAffiliatePayments(params),
                getAffiliates: getAffiliates(params),
                getAffiliatesLinkedinIds: getAffiliatesLinkedinIds(params),
                getAffiliatesSummaryFromLastLogin: getAffiliatesSummaryFromLastLogin(params),
                getAffiliateSummary: getAffiliateSummary(params),
            },
            affiliationErrors: {
                getAffiliationErrors: getAffiliationErrors(params),
                setAffiliationErrorsProcessed: setAffiliationErrorsProcessed(params),
            },
            ambassadors: {
                affiliation: {
                    createAffiliation: createAffiliation(params),
                    getAffiliation: getAffiliation(params),
                },
                contracts: {
                    acceptContract: acceptContract(params),
                },
                language: {
                    getLanguage: getLanguage(params),
                    setLanguage: setLanguage(params),
                },
                links: {
                    createLink: createLink(params),
                    deleteLink: deleteLink(params),
                    getLinks: getLinks(params),
                    getSummary: getSummary(params),
                    updateLink: updateLink(params),
                },
                payment: {
                    getPayments: getPayments(params),
                    getPreviousPaymentAmount: getPreviousPaymentAmount(params),
                },
                profile: {
                    getIntercomIdentifiers: getIntercomIdentifiers(params),
                    getProfile: getProfile(params),
                    updatePassword: updatePassword(params),
                    updateProfile: updateProfile(params),
                },
                sendInviteToUser: sendInviteToUser(params),
                wallets: {
                    confirmTransfer: confirmTransfer(params),
                    getWallet: getWallet(params),
                    prepareGeneratedInvoice: prepareGeneratedInvoice(params),
                    verifyInvoice: verifyInvoice(params),
                },
                stripeAccount: {
                    startStripeOnboarding: startStripeOnboarding(params),
                },
            },
            waalaxy: {
                linkAccount: linkAccount(params),
            },
        },
        public: {
            ambassadors: {
                checkPasswordToken: checkPasswordToken(params),
                forgetPassword: forgetPassword(params),
                impersonate: impersonate(params),
                login: login(params),
                resetPassword: resetPassword(params),
                signUp: signUp(params),
            },
            stripe: {
                getCountryList: getCountryList(params),
                webhooks: webhooks(params),
                webhooksAccount: webhooksAccount(params),
            },
        },
    };
};
